<section class="session-route">
  <header class="session-route__header">
    <div>
      <p class="session-route__eyebrow">Session</p>
      <h1>Room controls</h1>
      <p class="session-route__subtitle">
        Share your code, lock room access, and control how playback fans out.
      </p>
    </div>
  </header>

  <div class="session-route__grid">
    <article class="session-route__panel session-route__panel--hero">
      <p class="session-route__label">Session Code</p>
      <div class="session-route__code">{{ sessionCode }}</div>

      <div class="session-route__actions">
        <button
          class="session-route__button session-route__button--ghost"
          type="button"
          (click)="onCopySessionCode()"
        >
          Copy Code
        </button>
        <button
          class="session-route__button session-route__button--ghost"
          type="button"
          (click)="onShareSessionCode()"
        >
          Share
        </button>
        <button
          class="session-route__button session-route__button--danger"
          type="button"
          (click)="onExitSession()"
        >
          {{ primaryActionLabel }}
        </button>
      </div>

      <div class="session-route__qr-block" *ngIf="sessionJoinUrl">
        <div class="session-route__qr-frame" *ngIf="qrCodeDataUrl; else qrFallback">
          <img
            class="session-route__qr-image"
            [src]="qrCodeDataUrl"
            [alt]="'Scan to join Queueify session ' + sessionCode"
          />
        </div>
        <ng-template #qrFallback>
          <div class="session-route__qr-frame session-route__qr-frame--fallback">
            <span class="material-symbols-outlined">qr_code_2</span>
          </div>
        </ng-template>

        <p class="session-route__copy">
          Scan to open Queueify and join this session.
        </p>
        <div class="session-route__qr-actions">
          <button
            class="session-route__button session-route__button--ghost"
            type="button"
            (click)="onCopySessionLink()"
          >
            Copy Link
          </button>
          <button
            class="session-route__button session-route__button--ghost"
            type="button"
            (click)="onShareSessionLink()"
          >
            Share Link
          </button>
        </div>
      </div>

      <p class="session-route__feedback" *ngIf="feedbackMessage">
        {{ feedbackMessage }}
      </p>
    </article>

    <article class="session-route__panel" *ngIf="sessionState.isOwner; else memberInfo">
      <p class="session-route__label">Host Settings</p>
      <h2>Control room access and playback</h2>
      <p class="session-route__copy">
        These settings update the live room immediately and stay enforced by the
        backend.
      </p>

      <div class="session-route__loading" *ngIf="isLoadingSettings">
        Loading room settings...
      </div>

      <div *ngIf="!isLoadingSettings && settings as liveSettings">
        <div class="session-route__settings">
          <div class="session-route__setting">
            <div>
              <h3>Disallow anonymous users</h3>
              <p>Require Spotify-authenticated users to join this room.</p>
            </div>
            <button
              class="session-route__toggle"
              type="button"
              [class.session-route__toggle--active]="liveSettings.disallow_anonymous_users"
              [disabled]="isSavingSettings"
              (click)="onToggleDisallowAnonymousUsers(!liveSettings.disallow_anonymous_users)"
            >
              <span></span>
            </button>
          </div>

          <div class="session-route__setting session-route__setting--stacked">
            <div>
              <h3>Session type</h3>
              <p>Choose whether playback stays on the host or fans out to ready members.</p>
            </div>
            <div class="session-route__segment">
              <button
                class="session-route__segment-button"
                type="button"
                [class.session-route__segment-button--active]="liveSettings.session_type === 'host_only'"
                [disabled]="isSavingSettings"
                (click)="onSetSessionType('host_only')"
              >
                Host Only
              </button>
              <span
                class="session-route__segment-tooltip"
                [attr.title]="everyoneModeTooltip || null"
              >
                <button
                  class="session-route__segment-button"
                  type="button"
                  [class.session-route__segment-button--active]="liveSettings.session_type === 'everyone'"
                  [class.session-route__segment-button--blocked]="everyoneModeDisabled"
                  [disabled]="isSavingSettings || everyoneModeDisabled"
                  (click)="onSetSessionType('everyone')"
                >
                  Everyone
                </button>
              </span>
            </div>
            <p class="session-route__hint">
              Everyone mode requires Disallow anonymous users.
            </p>
          </div>

          <div class="session-route__setting">
            <div>
              <h3>Explicit filter</h3>
              <p>Block explicit tracks from being queued or started in this room.</p>
            </div>
            <button
              class="session-route__toggle"
              type="button"
              [class.session-route__toggle--active]="liveSettings.explicit_filter"
              [disabled]="isSavingSettings"
              (click)="onToggleExplicitFilter(!liveSettings.explicit_filter)"
            >
              <span></span>
            </button>
          </div>
        </div>

        <div class="session-route__sync-panel">
          <div class="session-route__sync-header">
            <div>
              <p class="session-route__label">Everyone Playback</p>
              <h3>{{ sessionTypeLabel(liveSettings.session_type) }}</h3>
            </div>
            <div class="session-route__sync-counts">
              <span>{{ liveSettings.everyone_playback_status.ready_member_count }} ready</span>
              <span>{{ liveSettings.everyone_playback_status.unsynced_member_count }} blocked</span>
            </div>
          </div>
          <p class="session-route__copy">{{ everyoneSummary }}</p>

          <div class="session-route__sync-list">
            <article
              class="session-route__sync-row"
              *ngFor="let member of liveSettings.member_sync_status"
            >
              <div class="session-route__sync-copy">
                <div class="session-route__sync-title">
                  <h4>{{ member.display_name }}</h4>
                  <span class="session-route__host-badge" *ngIf="member.is_host">
                    Host
                  </span>
                  <span class="session-route__host-badge" *ngIf="member.is_leader">
                    Leader
                  </span>
                </div>
                <p>{{ member.status_message }}</p>
              </div>
              <span
                class="session-route__sync-pill"
                [ngClass]="syncStateClass(member)"
              >
                {{ syncStateLabel(member) }}
              </span>
            </article>
          </div>
        </div>
      </div>

      <p class="session-route__error" *ngIf="settingsError">{{ settingsError }}</p>
      <p class="session-route__feedback" *ngIf="settingsFeedback">
        {{ settingsFeedback }}
      </p>
    </article>
  </div>
</section>

<ng-template #memberInfo>
  <article class="session-route__panel">
    <p class="session-route__label">Status</p>
    <h2>Joined as a listener</h2>
    <p class="session-route__copy">
      Only the host can change room settings. You can still copy or share the
      code from this view.
    </p>
  </article>
</ng-template>
